<template>
  <div class="view home">
    <div class="header">
      <div class="content">
        <div class="title">
          <span class="white">{{ $t('hello') }} {{ user.firstName }}, </span>
          <span>{{ $t('welcomeHome') }}</span>
        </div>

        <div class="main-container">
          <div class="info-container">
            <div class="title nomargin bold">{{ $t('supervisedCompany') }}</div>

            <div class="content">
              <div class="item">
                <div class="value">{{ summary.wallet || '-' }}</div>
                <div class="text">{{ $t('managedByYou') }}</div>
              </div>
              <div class="item">
                <div class="value">{{ summary.total || '-' }}</div>
                <div class="text">{{ $t('inTotal') }}</div>
              </div>
            </div>

            <div class="line" />

            <div class="title bold">{{ $t('monitoringLevel') }}</div>

            <div class="content column">
              <div v-for="line in stats" :key="line.id" class="line-container">
                <div class="title-line">{{ line.monitoringType }}</div>
                <div class="line-content">
                  <div class="line-percent background"></div>
                  <div class="line-percent" :class="line.type" :style="{ width: `${line.percent * 2}px` }"></div>
                  <div class="value">{{ line.percent >= 1 ? line.percent : '&lt; 1' }}%</div>
                </div>
              </div>
            </div>

            <div class="button main-button" @click="$router.push('/wallet')">{{ $t('goToWallet') }}</div>
          </div>

          <div class="search-container fullSize">
            <div class="title">{{ $t('addCompanyToInfogreffe') }}</div>
            <div class="sub-title">{{ $t('addCompanyToInfogreffeSubtitle') }}</div>

            <div class="input-container">
              <input v-model="inputSearchCompany" type="text" :placeholder="$t('companySearchPlaceholder')" class="input" @keydown.enter="search" />
              <div class="icon-container"><IconSearch class="icon-search" @click="search" /></div>
            </div>

            <div v-if="user.isMainUser" class="import-container hide-small">
              <span>{{ $t('or') }}</span>

              <div class="main-button">
                <IconImport class="icon icon-import"></IconImport>
                <span @click="$router.push('/importCompanies')">{{ $t('importCompanyList') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <div class="title-line">
        <div class="title">{{ $t('recentAlerts') }}</div>
        <div class="button main-button hide-small" @click="$router.push('/alertes')">{{ $t('allAlerts') }}</div>
      </div>

      <AlertTable
        v-if="loaded"
        :users="users"
        :packages="packages"
        :config="{
          pagination: false,
          maxElement: 5,
          internalUserOnly: true,
          headingFilters: { denomination: true, monitoringLevel: true, event: false, manager: false },
        }"
        :filters="{ isFiltered: false }"
      />
      <div v-else class="lds-ring">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <div v-if="packages.length" class="title-line show-small">
        <div class="button main-button" @click="$router.push('/alertes')">{{ $t('allAlerts') }}</div>
      </div>

      <div v-if="news.length" class="title-line">
        <div class="title">{{ $t('lastNews') }}</div>
        <div class="button main-button hide-small" @click="$router.push({ name: 'news' })">{{ $t('allNews') }}</div>
      </div>

      <News :news="news" />

      <div class="title-line show-small">
        <div class="button main-button" @click="$router.push({ name: 'news' })">{{ $t('allNews') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import News from '@/components/News';
import AlertTable from '@/components/tables/AlertTable';

export default {
  components: { AlertTable, News },

  data() {
    return {
      summary: {
        total: 0,
        wallet: 0,
      },
      stats: [],
      news: [],
      users: [],
      packages: [],
      inputSearchCompany: '',
      loaded: false,
    };
  },

  computed: {
    ...mapGetters(['isLogged', 'user']),

    isPercentageDisabled() {
      if (!this.stats.length) return false;
      const value = this.stats.reduce((accumulator, currentValue) =>
        accumulator.value ? accumulator.value + currentValue.value : accumulator + currentValue.value,
      );
      return value !== 0;
    },
  },

  async mounted() {
    this.news = (await this.$store.dispatch('getNews')).items;
    this.users = (await this.$store.dispatch('getUsers', { id: this.user.client.id })).users;
    this.setUpHomeInfos(await this.$store.dispatch('getHomeInfos'));
    this.packages = (await this.$store.dispatch('getPackagesLevel')).items;
    this.loaded = true;
  },

  methods: {
    async search() {
      if (this.inputSearchCompany.trim().length === 0) return;

      const isSiren = Number(this.inputSearchCompany.replace(/\s/g, ''));
      if (isSiren && isSiren.toString().length >= 6) {
        this.inputSearchCompany = this.inputSearchCompany.replace(/\s/g, '');
      }

      this.$router.push({ name: 'search', params: { searchedCompanies: this.inputSearchCompany } });
    },

    calculatePercentage() {
      let stats = this.stats.sort((a, b) => b.value - a.value);
      for (let index = 0; index < stats.length; index++) {
        stats[index].percent = stats[index].value === 0 ? 0 : ((100 * stats[index].value) / this.summary.total).toFixed(1);
      }
      this.stats = stats.sort((a, b) => a.id - b.id);
    },

    setUpHomeInfos(result) {
      const array = [];
      array.push({ value: result.hight, type: 'stronger', id: 0, monitoringType: '7 à 9' });
      array.push({ value: result.medium, type: 'simplified', id: 1, monitoringType: '4 à 6' });
      array.push({ value: result.low, type: 'normal', id: 2, monitoringType: '1 à 3' });
      array.push({ value: result.disabled, type: 'inactive', id: 3, monitoringType: '0' });
      this.stats = array;
      this.summary.total = result.all;
      this.summary.wallet = result.managedByUser;
      this.calculatePercentage();
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/variables.scss';
@use '@/styles/fonts.scss';
.view {
  .input {
    width: 100%;
    height: 48px;
    margin: 0 0 16px;
    padding: 12px 16px;
    box-sizing: border-box;
    color: colors.$text-color;
    border: solid 1px colors.$lightgrey-color;
    border-radius: 4px;
    background: colors.$white-color;

    &:last-child {
      margin: 0;
    }

    &::placeholder {
      color: colors.$lightgrey-color;
    }

    &.incorrect {
      border-color: colors.$error-color;
    }
  }

  .header {
    background: colors.$main-color;
    padding: 90px 0 64px;
    box-sizing: border-box;
  }

  .content {
    .title {
      margin: 50px 0 24px;

      &.bold {
        font-weight: 500;
      }

      &.nomargin {
        margin: 0 0 24px;
      }

      span {
        color: colors.$darkblue-color;
        font-size: 25px;
        font-weight: 500;

        &.white {
          color: colors.$white-color;
        }
      }
    }

    .main-container {
      display: flex;
      flex-grow: 1;

      @media only screen and (max-width: responsive.$layout-breakpoint-medium) {
        flex-wrap: wrap;
      }
    }

    .info-container {
      width: 386px !important;
      height: 500px;
      padding: 40px 32px;
      margin: 8px 24px 8px 0;
      box-sizing: border-box;
      background: colors.$white-color;
      border-radius: 6px;
      box-shadow: 0 4px 32px variables.$box-shadow;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;

      .title {
        margin-top: 0;
        font-size: 14px;
        margin-bottom: 16px;
        color: colors.$black-color;
      }

      .content {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 100%;

        &.column {
          align-items: flex-start;
          flex-direction: column;
        }

        .item {
          color: colors.$lightblue-color;

          .value {
            font-size: 35px;
            line-height: 41px;
            font-weight: 500;
          }

          .text {
            height: 34px;
            font-size: 14px;
          }

          &:nth-child(2n) {
            margin-left: 20px;
          }
        }

        .line-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-direction: column;
          width: 100%;
          min-height: 44px;

          .title-line {
            margin: 0;
            font-weight: 500;
            font-size: 11px;
            align-self: flex-start;
            color: colors.$lightgrey-color;
          }

          .line-content {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative;

            .line-percent {
              position: absolute;
              left: 0;
              right: 0;
              top: 0;
              bottom: 0;
              max-width: 75%;
              height: 10px;
              border-radius: 5px;
              margin: 4px 0 12px 0;
              min-width: 10px;
              background: rgba(colors.$lightgrey-color, 0.2);

              &.stronger {
                background: colors.$red-color;
              }

              &.simplified {
                background: colors.$orange-color;
              }

              &.normal {
                background: colors.$lightgreen-color;
              }

              &.inactive {
                background: colors.$grey-color;
              }
            }

            .value {
              width: 100%;
              text-align: right;
              font-size: 13px;
              color: colors.$darkblue-color;
              margin: 0 0 12px 0;
            }
          }
        }
      }

      .line {
        width: 100%;
        height: 1px;
        background: rgba(colors.$lightgrey-color, 0.3);
        flex-shrink: 0;
        margin: 18px 0;
      }

      .button {
        align-self: flex-end;
        margin: 24px auto 0;
        font-size: 16px;
        padding: 10px 23px;
        flex-shrink: 0;
      }

      @media only screen and (max-width: responsive.$layout-breakpoint-medium) {
        margin: 0;
        flex-grow: 1;
        width: 100%;
      }
    }

    .search-container {
      max-width: 70%;
      height: auto;
      padding: 100px;
      margin: 8px 0 8px 0;
      box-sizing: border-box;
      background: colors.$blue-color;
      border-radius: 6px;
      box-shadow: 0 4px 32px variables.$box-shadow;

      &.fullSize {
        width: 100%;
      }

      .title {
        color: colors.$white-color;
        font-size: 20px;
        font-weight: 500;
        margin: 0 0 24px;
      }

      .sub-title {
        font-size: 15px;
        margin: 0 0 48px;
        color: colors.$white-color;
        max-width: 600px;
      }

      .input-container {
        margin: 0;
        display: flex;

        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 46px;
          padding: 0;
          color: colors.$lightblue-color;
          border-radius: 4px;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border: solid 1px colors.$lightgrey-color;
          background: colors.$white-color;
          border-left: none;

          &:hover {
            cursor: pointer;

            .icon-search {
              transform: scale(1.1);
            }
          }
        }

        .input {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-right: none;
          text-overflow: ellipsis;
        }
      }

      .import-container {
        display: flex;
        flex-direction: column;

        span:first-child {
          font-style: italic;
          margin-bottom: 12px;
        }

        .main-button {
          background: colors.$lightgreen-color;

          min-width: 48px;
          width: 400px;
          padding: 0 0;
          border-radius: 5px;

          .icon {
            margin-right: 12px;
          }
        }

        span {
          color: colors.$white-color;
          margin: 0 4px 0 0;
          text-decoration: none;
          font-size: 15px;

          &:nth-child(2n) {
            color: colors.$white-color;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
              opacity: 0.8;
            }
          }
        }
      }

      @media only screen and (max-width: responsive.$layout-breakpoint-medium) {
        margin: 8px 0 0;
        flex-grow: 1;
        width: 100%;
      }
    }
  }

  .title-line {
    display: flex;
    justify-content: space-between;
    margin: 72px 0 0;
    align-items: center;

    @media only screen and (max-width: responsive.$layout-breakpoint-medium) {
      justify-content: center;
      margin: 24px 0 8px;
    }

    .title {
      font-family: fonts.$title-font;
      font-size: 24px;
      font-weight: 500;
      color: colors.$darkblue-color;
      flex-grow: 1;
      text-align: left;
      margin: 0 0 0px;

      @media only screen and (max-width: responsive.$layout-breakpoint-medium) {
        margin: 0;
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .view {
    .header {
      padding-bottom: 16px;
    }

    .content {
      .main-container {
        display: flex;
        flex-direction: column;

        .info-container {
          padding: 40px 32px;
          width: 100% !important;
          margin-bottom: 16px;
        }

        .search-container {
          padding: 40px 32px;
          min-width: 100%;

          .sub-title {
            margin-bottom: 24px;
          }

          .input-container {
            margin-bottom: 0;
          }
        }
      }

      .title-line {
        margin-top: 48px;
      }
    }
  }
}
</style>
